import { debounce } from 'lodash';
import { ChangeEvent, Dispatch, SyntheticEvent, useMemo } from 'react';
import { PendingRespondentStatus, SearchParams } from 'src/@types/pendingRespondentStatus';
import { StatusOption } from '../IdIssuanceManagementSearchForm';
import { Action, actions } from '../utils/searchState';

type UseIdIssuanceManagementSearchFormProps = {
  fetchPendingRespondents: (searchParams: SearchParams) => Promise<void>;
  searchParams: SearchParams;
  dispatch: Dispatch<Action>;
};

export const useIdIssuanceManagementSearchForm = ({ fetchPendingRespondents, searchParams, dispatch }: UseIdIssuanceManagementSearchFormProps) => {
  const debouncedFetch = useMemo(
    () =>
      debounce(async (searchText: string, status: PendingRespondentStatus | 'ALL', deletable: boolean) => {
        await fetchPendingRespondents({ ...searchParams, searchText, status, deletable, currentPage: 1 });
      }, 1000),
    [fetchPendingRespondents, searchParams],
  );

  const onSearchTextChange = async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch(actions.setSearchText(event.target.value));
    await debouncedFetch(event.target.value, searchParams.status, searchParams.deletable);
  };

  const onStatusChange = async (_: SyntheticEvent<Element, Event>, newValue: StatusOption) => {
    dispatch(actions.setStatus(newValue.id));
    await debouncedFetch(searchParams.searchText, newValue.id as PendingRespondentStatus | 'ALL', searchParams.deletable);
  };

  const onDeletableChange = async (e: ChangeEvent<HTMLInputElement>) => {
    dispatch(actions.setDeletable(e.target.checked));
    await debouncedFetch(searchParams.searchText, searchParams.status, e.target.checked);
  };

  const clearSearchText = async () => {
    dispatch(actions.setSearchText(''));
    await debouncedFetch('', searchParams.status, searchParams.deletable);
  };

  const refresh = async () => {
    await debouncedFetch(searchParams.searchText, searchParams.status, searchParams.deletable);
  };

  return {
    onSearchTextChange,
    onStatusChange,
    onDeletableChange,
    clearSearchText,
    refresh,
  };
};
